<template>
  <b-card no-body>
    <b-card-header>
      <h5 class="h3 mb-0">{{ usedProps.title }}</h5>
      Average : {{ getValue(avg) }}
    </b-card-header>
    <b-card-body>
      <b-container fluid="xl">
        <b-row align="top">
          <b-col cols="4">
            <b-tabs v-if="tabProps" v-model="tabIndex" :vertical=true>
              <b-tab v-for="(tab, index) in tabProps" :key="tab.title" :title="tab.title"></b-tab>
            </b-tabs>
          </b-col>
          <b-col cols="8">
            <b-container fluid="md">
              <b-list-group flush class="list my--3">
                <b-list-group-item v-for="(item, index) in values.slice(0, usedProps.items)"
                                   :key="item[usedProps.keyProperty]"
                                   class="px-0">
                  <b-row align-v="center">
                    <b-col cols="6">
                      <div v-if="item[usedProps.keyProperty] === 'UNKNOWN'">
                        <h3>
                        {{ item[usedProps.keyProperty] }}
                        </h3>
                      </div>
                      <div v-else>
                        <h3>
                        <router-link :to="{ name: routeName, params: { type: type, entityId: item[usedProps.keyProperty] }}">
                          {{ item[usedProps.labelProperty] }}
                        </router-link>
                        </h3>
                      </div>
                    </b-col>
                    <b-col cols="6" align="right">
                      <h4>{{ getValue(item[usedProps.valueProperty]) }}</h4>
                      <base-progress class="progress-xs mb-0"
                                     :type="getPercentage(item[usedProps.valueProperty]) >= 100? 'green' : 'red'"
                                     :value="getPercentage(item[usedProps.valueProperty])">
                      </base-progress>
                      <div v-if="usedProps.showPercentage">
                        <div v-if="getPercentage(item[usedProps.valueProperty]) >= 100">
                          <span class="text-success mr-2"><i
                            class="fa fa-arrow-up"></i> {{ getPercentage(item[usedProps.valueProperty]) - 100 }}%</span>
                        </div>
                        <div v-else>
                          <span class="text-danger mr-2"><i
                            class="fa fa-arrow-down"></i> {{ getPercentage(item[usedProps.valueProperty]) }}%</span>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-list-group-item>
                <b-row v-if="values.length > usedProps.items" align-v="center" cols="12">
                  <b-col cols="6"><span class="text-success mr-2"><i class="fa fa-arrow-up"></i> Best {{ usedProps.items }}</span>
                  </b-col>
                  <b-col cols="6" align="right"><span class="text-danger mr-2" align="right">Worst {{ usedProps.items }} <i
                    class="fa fa-arrow-down"></i></span></b-col>
                </b-row>
                <b-list-group-item v-if="values.length > usedProps.items" v-for="(item, index) in values.slice(-usedProps.items)"
                                   :key="item[usedProps.keyProperty]"
                                   class="px-0">
                  <b-row align-v="center">
                    <b-col cols="6">
                      <h3>
                        <router-link :to="{ name: routeName, params: { type: type, entityId: item[usedProps.keyProperty] }}">
                          {{ item[usedProps.labelProperty] }}
                        </router-link>
                      </h3>
                    </b-col>
                    <b-col cols="6" align="right">
                      <h4>{{ getValue(item[usedProps.valueProperty]) }}</h4>
                      <base-progress class="progress-xs mb-0"
                                     :type="getPercentage(item[usedProps.valueProperty]) >= 100? 'green' : 'red'"
                                     :value="getPercentage(item[usedProps.valueProperty])">
                      </base-progress>
                      <div v-if="usedProps.showPercentage">
                        <div v-if="getPercentage(item[usedProps.valueProperty]) >= 100">
                          <span class="text-success mr-2"><i
                            class="fa fa-arrow-up"></i> {{ getPercentage(item[usedProps.valueProperty]) - 100 }}%</span>
                        </div>
                        <div v-else>
                          <span class="text-danger mr-2"><i
                            class="fa fa-arrow-down"></i> {{ getPercentage(item[usedProps.valueProperty]) }}%</span>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
            </b-container>
          </b-col>
        </b-row>
      </b-container>
    </b-card-body>
  </b-card>
</template>

<script>

// https://michaelnthiessen.com/pass-function-as-prop/

import Tabs from "../../components/Tabs/Tabs";
import TabPane from "../../components/Tabs/Tab";
import Collapse from "../../components/Collapse/Collapse";
import {formatDistance} from '../../util/formatting';
import {formatDuration} from '../../util/formatting';

export default {
  name: "driving-style-card",
  components: {
    Tabs,
    TabPane,
    Collapse
  },
  props: {
    type: {
      type: String,
      default: 'ASSET',
      description: "Types : ASSET, DRIVER"
    },
    title: {
      type: String,
      description: "Title text"
    },
    unit: {
      type: String,
      default: "",
      description: "Unit label of represented value"
    },
    decimal: {
      type: Number,
      default: 0,
      description: "Number of decimals"
    },
    items: {
      type: Number,
      default: 50,
      description: "Number of best and worst values to show"
    },
    tabProps: {
      type: Array,
      description: "Array of properties for tab representation"
    },
    data: {
      type: Array,
      description: "Array of values"
    },
    sortDesc: {
      type: Boolean,
      default: true,
      description: "Whether to sort with descending values"
    },
    keyProperty: {
      type: String,
      default: 'id',
      description: "Key property name"
    },
    labelProperty: {
      type: String,
      default: 'name',
      description: "Label property name"
    },
    valueProperty: {
      type: String,
      default: undefined,
      description: "Value property name"
    },
    formatValue: {
      type: Function,
      default: undefined,
      description: "Method to format the value"
    },
    showPercentage: {
      type: Boolean,
      default: false,
      description: 'Whether percentage should be shown'
    }
  },
  data: _ => ({
    max: 0,
    avg: 0,
    min: Number.MAX_VALUE,
    values: [],
    tabIndex: 0,
    usedProps: {}
  }),
  computed: {
    routeName() {
      if (this.type === 'ASSET') {
        return 'asset-detail'
      } else {
        return 'driver-detail'
      }
    }
  },
  methods: {
    setProperties() {
      let prop = this.tabProps[this.tabIndex]

      if (prop === undefined) {
        this.usedProps.title = this.title
        this.usedProps.valueProperty = this.valueProperty
        this.usedProps.decimal = this.decimal
        this.usedProps.unit = this.unit
        this.usedProps.formatValue = this.formatValue
        this.usedProps.items = this.items
        this.usedProps.showPercentage = this.showPercentage
        this.usedProps.sortDesc = this.sortDesc
        this.usedProps.keyProperty = this.keyProperty
        this.usedProps.labelProperty = this.labelProperty

        return
      }

      this.usedProps.title = prop.title
      this.usedProps.valueProperty = prop.valueProperty
      if (prop.decimal !== undefined) {
        this.usedProps.decimal = prop.decimal
      } else {
        this.usedProps.decimal = 0
      }
      if (prop.unit !== undefined) {
        this.usedProps.unit = prop.unit
      } else {
        this.usedProps.unit = ""
      }
      if (prop.formatValue !== undefined) {
        if ("distance" === prop.formatValue) {
          this.usedProps.formatValue = formatDistance
        } else if ("duration" === prop.formatValue) {
          this.usedProps.formatValue = formatDuration
        } else {
          this.usedProps.formatValue = undefined
        }
      } else {
        this.usedProps.formatValue = undefined
      }
      if (prop.items !== undefined) {
        this.usedProps.items = prop.items
      } else {
        this.usedProps.items = 50
      }
      if (prop.showPercentage !== undefined) {
        this.usedProps.showPercentage = prop.showPercentage
      } else {
        this.usedProps.showPercentage = false
      }
      if (prop.sortDesc !== undefined) {
        this.usedProps.sortDesc = prop.sortDesc
      } else {
        this.usedProps.sortDesc = true
      }
      if (prop.keyProperty !== undefined) {
        this.usedProps.keyProperty = prop.keyProperty
      } else {
        this.usedProps.keyProperty = "id"
      }
      if (prop.labelProperty !== undefined) {
        this.usedProps.labelProperty = prop.labelProperty
      } else {
        this.usedProps.labelProperty = "name"
      }
    },
    getPercentage(value) {
      if (value === undefined) {
        value = 0
      }

      let newVal = value
      if (newVal === 0) {
        newVal = 0.1
      }

      let percent = this.usedProps.sortDesc ? parseInt((newVal / this.avg) * 100) : parseInt((this.avg / newVal) * 100)

      if (percent < 100) {
        return percent
      } else {
        if (percent > 100) {
          return 100
        } else {
          return percent
        }
      }
    },
    getValue(value) {
      if (value === null) {
        return '- ' + this.usedProps.unit
      } else if (!value) {
        return '0 ' + this.usedProps.unit
      }

      return (this.usedProps.formatValue ? this.usedProps.formatValue(value) : this.usedProps.decimal ? value.toFixed(this.usedProps.decimal) : value) + ' ' + this.usedProps.unit
    },
    calculateData() {
      this.setProperties()

      this.min = Number.MAX_VALUE
      this.max = 0
      this.avg = 0

      this.values = JSON.parse(JSON.stringify(this.data)); // TODO Expensive - to avoid watch loop!!

      let property = this.usedProps.valueProperty

      if (this.usedProps.sortDesc) {
        this.values.sort(function (p1, p2) {
          return p2[property] - p1[property];
        })
      } else {
        this.values.sort(function (p1, p2) {
          return p1[property] - p2[property];
        })
      }

      let countAvg = 0
      for (let val of this.values) {
        let value = val[property]

        if (value === undefined) {
          value = 0
        }

        if (value >= 0) {
          if (value > this.max) {
            this.max = value
          }
          if (value < this.min) {
            this.min = value
          }

          this.avg += value

          countAvg += 1
        }
      }

      this.avg = this.avg / countAvg
    }
  },
  watch: {
    data() {
      this.calculateData()
    },
    tabIndex() {
      this.setProperties()

      this.calculateData()
    }
  }
}
</script>

<style scoped>

</style>
